import React, { useState, useEffect, useRef } from 'react';
import { Eye, AlertCircle, Loader } from 'lucide-react';
import { templateApi, useSampleData } from '../../../hooks/useTemplates';

function TemplatePreview({ subjectTpl, bodyTpl, templateId }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const { sampleData } = useSampleData();

  useEffect(() => {
    if (!subjectTpl && !bodyTpl) {
      setPreview(null);
      setError(null);
      return;
    }

    if (!sampleData) return;

    // Debounce preview requests
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result;
        if (templateId) {
          result = await templateApi.preview(templateId, sampleData);
        } else {
          result = await templateApi.previewRaw({
            subjectTpl,
            bodyTpl,
            sampleData,
          });
        }
        setPreview(result.data);
      } catch (err) {
        const msg = err.response?.data?.error || 'Preview rendering failed';
        setError(msg);
        setPreview(null);
      } finally {
        setIsLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [subjectTpl, bodyTpl, templateId, sampleData]);

  return (
    <div className="template-preview">
      <div className="template-preview-header">
        <Eye size={16} />
        <span>Live Preview</span>
        {isLoading && <Loader size={14} className="template-preview-spinner" />}
      </div>

      <div className="template-preview-hint">
        Using sample data (Sarah Chen, 3-night stay)
      </div>

      {error ? (
        <div className="template-preview-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : preview ? (
        <>
          <div className="template-preview-subject">
            <label>Subject:</label>
            <span>{preview.subject}</span>
          </div>
          <div className="template-preview-body">
            <iframe
              title="Email Preview"
              srcDoc={preview.htmlBody}
              sandbox=""
              className="template-preview-iframe"
            />
          </div>
        </>
      ) : (
        <div className="template-preview-empty">
          Start typing to see a preview
        </div>
      )}
    </div>
  );
}

export default TemplatePreview;
